// @flow

import { CHANGED_PRODUCT_STATE } from '~/core/state/products/thermostat/constants';
import { delay } from 'redux-saga';
import { call, put, take, cancel, fork, select, race, takeLatest } from 'redux-saga/effects';
import type { Effect } from 'redux-saga';
import moment from 'moment-timezone';
import { getProducts } from '~/core/state/products/actions';
import { getRecipes } from '~/core/state/recipes/actions';
import { getActions } from '~/core/state/actions/actions';
import { getWeather } from '~/core/state/weather/actions';
import { getConfig } from '~/core/state/config/selectors';
import { canUseActions2 } from '~/core/state/hub/selectors';
import { flowRight, noop } from 'lodash/fp';
import { getPostcode, getUserCountry } from '~/core/state/user/selectors';
import { dateTimeInterval } from './actions';
import { logInComplete, logOut } from '~/core/state/application/actions';
import { INTERVAL, GET_PRODUCTS_INTERVAL, POLL_API_STOP, POLL_API_START } from './constants';
import handleSaga from '~/core/state/handle-saga';
import { serviceOutage as api } from '~/core/api';
import handleApi from '~/core/state/handle-api';
import { requestAlert } from '~/core/state/alerts/actions';
import { CHECK_SERVICE_OUTAGE_STATUS } from '~/core/state/service-outage/constants';
import { receiveServiceOutage } from '~/core/state/service-outage/actions';
import type { ServiceOutageStatusAction } from '~/core/state/service-outage/types';
import { alerts, feedback } from '~/core/constants';
import { loadDevices } from '~/core/state/devices/actions';

function* delayAction(interval, fn, ...params): Generator<noop | Effect, *, *> {
    while (true) {
        yield fn(...params.map(p => p()));
        yield call(delay, interval);
    }
}

function* getWeatherPoll(): Generator<Effect, *, *> {
    while (true) {
        const postcode = yield select(getPostcode);
        const countryCode = yield select(getUserCountry);
        yield put(getWeather({ postcode, countryCode }));
        yield call(delay, INTERVAL);
    }
}

function* weatherDaemonSaga(): Generator<Effect, *, *> {
    while (true) {
        const { error } = yield take(logInComplete);

        if (!error) {
            const pollToUpdate = yield fork(getWeatherPoll);
            yield take(logOut);
            yield cancel(pollToUpdate);
        }
    }
}

function* currentDateDaemonSaga(): Generator<noop, *, *> {
    yield* delayAction(INTERVAL, flowRight(put, dateTimeInterval), () => moment().valueOf());
}

function* getProductsAndRecipesPoll(): Generator<Effect, *, *> {
    const newActionsOn = yield select(getConfig, 'features:newActions');
    const hubActions2Capable = yield select(canUseActions2);
    const actions2Capable = newActionsOn && hubActions2Capable;

    while (true) {
        const { stopPoll } = yield race({
            timeout: call(delay, GET_PRODUCTS_INTERVAL),
            stopPoll: take(POLL_API_STOP)
        });

        if (stopPoll) {
            return;
        }

        yield put(loadDevices());

        if (actions2Capable) {
            yield put(getActions());
        } else {
            yield put(getRecipes());
        }

        yield put(getProducts());
    }
}

function* refreshWorker(): Generator<Effect, *, *> {
    let pollToUpdate = yield fork(getProductsAndRecipesPoll);

    while (yield take(CHANGED_PRODUCT_STATE)) {
        yield cancel(pollToUpdate);
        pollToUpdate = yield fork(getProductsAndRecipesPoll);
    }
}

function* refreshLoginProductsDaemonSaga(): Generator<Effect, *, *> {
    while (true) {
        const { error } = yield take(logInComplete);

        if (!error) {
            const worker = yield fork(refreshWorker);
            yield take(logOut);
            yield cancel(worker);
        }
    }
}

function* refreshProductsDaemonSaga(): Generator<Effect, *, *> {
    while (true) {
        const worker = yield fork(refreshWorker);
        yield take(logOut);
        yield cancel(worker);
    }
}

function* watchStartApiPoll(): Generator<Effect, *, *> {
    yield takeLatest(POLL_API_START, refreshProductsDaemonSaga);
}

export function* checkServiceOutageStatusSaga({
    payload: { serviceUrlBase, userLocale }
}: ServiceOutageStatusAction): Generator<noop | Effect, *, *> {
    while (true) {
        const result = yield call(handleApi(api.getServiceOutage), serviceUrlBase, userLocale);
        if (!result) {
            break;
        }

        const [serviceOutage, error] = result;
        yield put(receiveServiceOutage(error || serviceOutage));

        if (error) {
            yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
        }

        yield call(delay, 1200000);
    }
}

function* watchCheckServiceOutageStatusSaga(): Generator<Effect, *, *> {
    yield takeLatest(CHECK_SERVICE_OUTAGE_STATUS, handleSaga(checkServiceOutageStatusSaga));
}

export default [
    currentDateDaemonSaga,
    refreshLoginProductsDaemonSaga,
    weatherDaemonSaga,
    watchStartApiPoll,
    watchCheckServiceOutageStatusSaga
];



// WEBPACK FOOTER //
// ./src/core/state/hub/sagas.js